import { useEffect, useMemo, useState } from 'react'
import * as Sentry from '@sentry/react'
import {
  listSmsActivityForStaff,
  startSmsRecoveryForStaff,
  type SmsActivityRow,
  type SmsActivityResult,
  type SmsActivityWindow,
} from '../services/staff'
import {
  recoveryBucket,
  type RecoveryBucket,
} from '../utils/recoveryStepper'
import type { RecoveryDisplayState } from '../services/recoveryCases'
import { smsStatusMeta } from '../utils/smsActivityStatus'
import { formatMinutesAsTime } from '../utils/formatters'
import SmsActivitySlideout from './SmsActivitySlideout'
// Reuse the staff Run-History visual language (KPIs / chips / table / status).
import './DataSyncRunHistory.css'

/** "First Last", falling back to just whichever name half is present. "" when neither. */
function patientName(r: Pick<SmsActivityRow, 'patientFirstName' | 'patientLastName'>): string {
  return [r.patientFirstName, r.patientLastName].filter(Boolean).join(' ')
}

/** The appointment start time, or "" when there's no appointment or it's the
 *  schema default (no real time on file — showing it would misrepresent a guess
 *  as fact). */
function apptTime(r: Pick<SmsActivityRow, 'appointmentStartMin' | 'appointmentTimeIsDefault'>): string {
  if (r.appointmentTimeIsDefault) return ''
  return formatMinutesAsTime(r.appointmentStartMin)
}

function fmtAgo(iso: string | null): string {
  if (!iso) return '—'
  const ms = Date.now() - Date.parse(iso)
  if (!Number.isFinite(ms)) return '—'
  if (ms < 60_000) return 'now'
  const min = Math.floor(ms / 60_000)
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  return `${Math.floor(hr / 24)}d ago`
}

type QuickFilter = 'all' | string
type SortKey = 'smart' | 'practice' | 'clinic' | 'patient' | 'appointment' | 'status' | 'activity'

// #269: rerun_failed rows carry WHY they parked — "gave up after real
// progress" and "never got anything usable" triage differently, so the
// details cell says which this is.
const RERUN_REASON_LABEL: Record<string, string> = {
  rounds_exhausted: 'Progress made — ask limit reached; one staff question likely closes it.',
  repeat_fields: "Same field failed again — the patient's answer didn't fix it.",
  not_correctable: 'New failure is not patient-fixable (staff/ops class).',
  stale: 'Appointment passed / case aged out before the re-run.',
  no_data: 'No usable card read to re-run from.',
  not_executed: 'The re-run could not be built or executed.',
  no_linkage: 'Fired re-run linkage missing (legacy case).',
}

// Failures-first default: attention rows bubble up, then most-recent.
const BUCKET_RANK: Record<RecoveryBucket, number> = {
  staff_review: 0, in_progress: 1, waiting: 2, opted_out: 3,
  // Both terminal, so both sort last — but they stay separate buckets
  // everywhere else: a loop recovery and a hand-closed dead end are different
  // claims (see recoveryBucket).
  recovered: 4, closed_by_staff: 4,
}
// Chips: All + one per fine status (its count is the per-status stat), failures
// first. The reachable-today statuses always render (even at 0); the step-6
// re-check states auto-appear once they exist in the data.
const STATUS_CHIP_ORDER = [
  'awaiting_start', 'queued',
  'escalated', 'sms_failed', 'no_phone', 'cannot_extract', 'rerun_failed',
  'sms_sent', 'extracted', 'check_rerun',
  'opted_out', 'extracted_same', 'resolved',
]
const ALWAYS_SHOW_STATUSES = new Set([
  'awaiting_start',
  // `escalated` shows even at zero: a board that only grows the filter once a
  // patient has already been abandoned is the failure this set exists to stop.
  'escalated',
  'sms_failed', 'no_phone', 'cannot_extract', 'sms_sent', 'extracted', 'opted_out',
])

/** "Start flow" outcome → a short staff-facing note. Starting only STAMPS the
 *  flow (`queued`) — the scheduled sweep sends the text; the other outcomes are
 *  the instant no-SMS drains. */
function startOutcomeLabel(outcome: string): string {
  switch (outcome) {
    case 'queued': return 'Flow started — the text goes out with the next sweep pass inside the clinic’s SMS window.'
    case 'no_phone': return 'No phone number on file for this patient.'
    case 'sms_failed': return 'Failed to start — try again.'
    case 'skipped': return 'Not started (appointment passed or stale).'
    case 'sms_disabled': return 'Not started — no SMS number is assigned to this clinic or its organization.'
    case 'visit_cancelled': return 'Not started — this visit was cancelled.'
    case 'source_opt_out': return 'Not started — the practice’s record says not to text this patient.'
    default: return `Not started (${outcome}).`
  }
}

export default function SmsActivity() {
  const [data, setData] = useState<SmsActivityResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [window_, setWindow] = useState<SmsActivityWindow>('7d')
  const [quick, setQuick] = useState<QuickFilter>('all')
  const [practiceFilter, setPracticeFilter] = useState('all')
  const [sortKey, setSortKey] = useState<SortKey>('smart')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  // "Start flow": the checkId currently starting + per-check result notes.
  const [starting, setStarting] = useState<string | null>(null)
  const [startMsg, setStartMsg] = useState<Record<string, string>>({})
  // Thread slideout — the conversation row currently open (conversation rows
  // only; check/pending rows have no thread to show).
  const [openRow, setOpenRow] = useState<SmsActivityRow | null>(null)

  function refresh(w: SmsActivityWindow) {
    setLoading(true)
    setError(null)
    listSmsActivityForStaff(w)
      .then((r) => { setData(r); setLoading(false) })
      .catch((err) => {
        Sentry.captureException(err)
        setError(err instanceof Error ? err.message : 'Failed to load SMS activity.')
        setLoading(false)
      })
  }

  async function handleStart(row: SmsActivityRow) {
    const checkId = row.linkedCheckId
    if (!checkId || starting) return
    setStarting(checkId)
    setStartMsg((m) => { const n = { ...m }; delete n[checkId]; return n })
    try {
      const { outcome } = await startSmsRecoveryForStaff(row.orgId, checkId)
      // Every outcome leaves a note; queued also reloads so the row re-renders
      // with its Queued chip (and without the Start button).
      setStartMsg((m) => ({ ...m, [checkId]: startOutcomeLabel(outcome) }))
      if (outcome === 'queued') refresh(window_)
    } catch (err) {
      Sentry.captureException(err)
      setStartMsg((m) => ({ ...m, [checkId]: err instanceof Error ? err.message : 'Failed to start.' }))
    } finally {
      setStarting((s) => (s === checkId ? null : s))
    }
  }

  useEffect(() => { refresh(window_) }, [window_])

  const rows = useMemo(() => data?.activity ?? [], [data])
  // awaiting_start / queued aren't RecoveryDisplayStates — file them under
  // "waiting" for bucket math (waiting to be started / to send at window-open),
  // never passing them to recoveryBucket.
  const bucketOf = (r: SmsActivityRow): RecoveryBucket =>
    r.status === 'awaiting_start' || r.status === 'queued'
      ? 'waiting'
      : recoveryBucket(r.status as RecoveryDisplayState)

  const practices = useMemo(() => {
    const m = new Map<string, string>()
    rows.forEach((r) => m.set(r.orgId, r.practiceName))
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [rows])

  // Scoped by practice (drives KPIs + chip counts); quick chip filters the table.
  const scoped = useMemo(
    () => rows.filter((r) => practiceFilter === 'all' || r.orgId === practiceFilter),
    [rows, practiceFilter],
  )

  const statusCounts = useMemo(() => {
    const m = new Map<string, number>()
    scoped.forEach((r) => m.set(r.status, (m.get(r.status) ?? 0) + 1))
    return m
  }, [scoped])
  const statusChips = useMemo(() => {
    const known = STATUS_CHIP_ORDER.filter(
      (st) => ALWAYS_SHOW_STATUSES.has(st) || (statusCounts.get(st) ?? 0) > 0,
    )
    const extras = [...statusCounts.keys()].filter((st) => !STATUS_CHIP_ORDER.includes(st)).sort()
    return [...known, ...extras]
  }, [statusCounts])
  // The selected status can vanish (practice/window change) — fall back to All.
  const effectiveQuick = quick !== 'all' && !statusChips.includes(quick) ? 'all' : quick

  const filtered = useMemo(
    () => scoped.filter((r) => effectiveQuick === 'all' || r.status === effectiveQuick),
    [scoped, effectiveQuick],
  )

  const sorted = useMemo(() => {
    const arr = [...filtered]
    if (sortKey === 'smart') {
      // Awaiting-start (the actionable rollout cohort) bubbles above everything,
      // then failures, then most-recent.
      const rank = (r: SmsActivityRow) =>
        r.status === 'awaiting_start' ? -1 : BUCKET_RANK[bucketOf(r)]
      return arr.sort((a, b) =>
        rank(a) - rank(b) ||
        Date.parse(b.lastActivity || '') - Date.parse(a.lastActivity || ''))
    }
    const dir = sortDir === 'asc' ? 1 : -1
    const val = (r: SmsActivityRow): string | number => {
      switch (sortKey) {
        case 'practice': return r.practiceName.toLowerCase()
        case 'clinic': return r.clinicName.toLowerCase()
        case 'patient': return (patientName(r) || r.patientPhone || '').toLowerCase()
        case 'appointment': return r.appointmentTimeIsDefault ? -1 : r.appointmentStartMin ?? -1
        case 'status': return smsStatusMeta(r.status).label
        case 'activity': return Date.parse(r.lastActivity || '') || 0
        default: return 0
      }
    }
    return arr.sort((a, b) => {
      const av = val(a); const bv = val(b)
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir
      return String(av).localeCompare(String(bv)) * dir
    })
  }, [filtered, sortKey, sortDir])

  const summary = useMemo(() => ({
    total: scoped.length,
    awaitingStart: scoped.filter((r) => r.status === 'awaiting_start').length,
    needsAttention: scoped.filter((r) => bucketOf(r) === 'staff_review').length,
    awaiting: scoped.filter((r) => r.status === 'sms_sent').length,
    inProgress: scoped.filter((r) => r.status === 'extracted').length,
    optedOut: scoped.filter((r) => r.status === 'opted_out').length,
  }), [scoped])

  function toggleSort(k: SortKey) {
    if (k === sortKey) { setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')); return }
    setSortKey(k)
    setSortDir(k === 'activity' ? 'desc' : 'asc')
  }
  const sortArrow = (k: SortKey) => (sortKey === k ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '')

  const s = data ? summary : null

  return (
    <div className="dsh">
      <div className="dsh-head">
        <div>
          <h1>SMS Activity</h1>
          <div className="dsh-subt">Internal ops · patient SMS recovery flow · all practices</div>
        </div>
        <div className="dsh-head-meta">
          {data && <>Window: <b>{window_}</b> · {data.practiceCount} practices · {data.clinicCount} clinics</>}
          <button className="dsh-refresh" onClick={() => refresh(window_)} disabled={loading}>↻ {loading ? 'Refreshing…' : 'Refresh'}</button>
        </div>
      </div>

      <div className="dsh-kpis">
        <div className="dsh-kpi"><div className="dsh-kpi__l">Conversations</div><div className="dsh-kpi__n">{s?.total ?? '—'}</div><div className="dsh-kpi__d">in this window</div></div>
        <div className="dsh-kpi"><div className="dsh-kpi__l">Awaiting start</div><div className="dsh-kpi__n">{s?.awaitingStart ?? '—'}</div><div className="dsh-kpi__d">not texted yet · start manually</div></div>
        <div className={`dsh-kpi${(s?.needsAttention ?? 0) > 0 ? ' dsh-kpi--alert' : ''}`}><div className="dsh-kpi__l">Needs attention</div><div className="dsh-kpi__n dsh-kpi__n--bad">{s?.needsAttention ?? '—'}</div><div className="dsh-kpi__d">escalated · failed · unreadable · no phone</div></div>
        <div className="dsh-kpi"><div className="dsh-kpi__l">SMS sent</div><div className="dsh-kpi__n">{s?.awaiting ?? '—'}</div><div className="dsh-kpi__d">texted, no card yet</div></div>
        <div className="dsh-kpi"><div className="dsh-kpi__l">In progress</div><div className="dsh-kpi__n">{s?.inProgress ?? '—'}</div><div className="dsh-kpi__d">card received · extracted</div></div>
        <div className="dsh-kpi"><div className="dsh-kpi__l">Opted out</div><div className="dsh-kpi__n">{s?.optedOut ?? '—'}</div><div className="dsh-kpi__d">replied STOP</div></div>
      </div>

      <div className="dsh-qf">
        <button className={`dsh-chip${effectiveQuick === 'all' ? ' dsh-chip--on' : ''}`} onClick={() => setQuick('all')}>
          All <span className="dsh-c">{scoped.length}</span>
        </button>
        {statusChips.map((st) => {
          const meta = smsStatusMeta(st)
          return (
            <button
              key={st}
              title={st}
              className={`dsh-chip${effectiveQuick === st ? ' dsh-chip--on' : ''}${meta.tone === 'errored' ? ' dsh-chip--alert' : ''}`}
              onClick={() => setQuick(st)}
            >
              {meta.label} <span className="dsh-c">{statusCounts.get(st) ?? 0}</span>
            </button>
          )
        })}
        <div className="dsh-qf__right">
          <select className="dsh-ddl" value={practiceFilter} onChange={(e) => setPracticeFilter(e.target.value)}>
            <option value="all">All practices</option>
            {practices.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <select className="dsh-ddl" value={window_} onChange={(e) => setWindow(e.target.value as SmsActivityWindow)}>
            <option value="24h">Last 24h</option>
            <option value="7d">Last 7d</option>
            <option value="30d">Last 30d</option>
          </select>
        </div>
      </div>

      {error && <p className="dsh-error">{error}</p>}
      {loading && !data && <p className="dsh-loading">Loading SMS activity…</p>}

      {data && (
        <div className="dsh-card">
          <table className="dsh-table">
            <thead>
              <tr>
                <th className="dsh-sortable" onClick={() => toggleSort('practice')}>Practice{sortArrow('practice')}</th>
                <th className="dsh-sortable" onClick={() => toggleSort('clinic')}>Clinic{sortArrow('clinic')}</th>
                <th className="dsh-sortable" onClick={() => toggleSort('patient')}>Patient{sortArrow('patient')}</th>
                <th className="dsh-sortable" onClick={() => toggleSort('appointment')}>Appointment time{sortArrow('appointment')}</th>
                <th className="dsh-sortable" onClick={() => toggleSort('status')}>Status{sortArrow('status')}</th>
                <th className="dsh-sortable" onClick={() => toggleSort('activity')}>Last activity{sortArrow('activity')}</th>
                <th>Error</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && <tr><td colSpan={8} className="dsh-empty">No SMS activity matches these filters.</td></tr>}
              {sorted.map((r) => {
                const meta = smsStatusMeta(r.status)
                const name = patientName(r)
                const time = apptTime(r)
                // rerun_failed rows explain WHY in the details cell (#269).
                const detail =
                  r.error ??
                  (r.status === 'rerun_failed' && r.rerunFailureReason
                    ? RERUN_REASON_LABEL[r.rerunFailureReason] ?? r.rerunFailureReason
                    : null)
                return (
                  <tr
                    key={`${r.orgId}/${r.source}/${r.id}`}
                    className={r.error ? 'dsh-err-row' : ''}
                    onClick={r.source === 'case' ? () => setOpenRow(r) : undefined}
                    style={r.source === 'case' ? undefined : { cursor: 'default' }}
                  >
                    <td className="dsh-practice">{r.practiceName}</td>
                    <td className="dsh-clinic">{r.clinicName}</td>
                    <td className="dsh-ts">
                      {name ? (
                        <>
                          {name}
                          {r.patientPhone && <div className="dsh-sub">{r.patientPhone}</div>}
                        </>
                      ) : (r.patientPhone ?? '—')}
                    </td>
                    <td className="dsh-ts">{time || '—'}</td>
                    <td><span className={`dsh-status dsh-status--${meta.tone}`} title={r.status}><span className="dsh-d" />{meta.label}</span></td>
                    <td className="dsh-ts">{fmtAgo(r.lastActivity || null)}</td>
                    <td>{detail ? <span className="dsh-snip" title={detail}>{detail}</span> : <span className="dsh-ts">—</span>}</td>
                    <td>
                      {r.status === 'awaiting_start' && r.linkedCheckId && (
                        <button
                          className="dsh-refresh"
                          // One start at a time (handleStart no-ops while one is in
                          // flight) — disable EVERY row's button meanwhile, not just
                          // the active one, so a click never silently does nothing.
                          disabled={starting !== null}
                          onClick={() => handleStart(r)}
                        >
                          {starting === r.linkedCheckId ? 'Starting…' : 'Start flow'}
                        </button>
                      )}
                      {/* The note outlives the button: a queued row (no button)
                          keeps showing when its text will go out. */}
                      {r.linkedCheckId && startMsg[r.linkedCheckId] ? (
                        <div className="dsh-snip" title={startMsg[r.linkedCheckId]}>{startMsg[r.linkedCheckId]}</div>
                      ) : (
                        r.status !== 'awaiting_start' && <span className="dsh-ts">—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {data.capped && <p className="dsh-capped">
            Some sources hit the row cap — the oldest rows in this window (including failures among them) are not shown. Narrow the window to see them.
          </p>}
        </div>
      )}

      {openRow && (
        <SmsActivitySlideout
          key={`${openRow.orgId}/${openRow.id}`}
          row={openRow}
          onClose={() => setOpenRow(null)}
        />
      )}
    </div>
  )
}
